import { useState, useEffect } from "react";
import { Menu, X, ChevronDown, MessageCircle, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import logo from "@/assets/logo.png";
import LanguageSelector from "@/components/LanguageSelector";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const Navbar = () => {
  const { t } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Scroll to contact section when navigating from another page
  useEffect(() => {
    if (location.pathname === "/" && location.hash === "#contact") {
      setTimeout(() => {
        const element = document.querySelector("#contact");
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }, 100);
    }
  }, [location]);

  const handleNavClick = (e: React.MouseEvent, href: string) => {
    setIsMobileMenuOpen(false);
    
    // If it's the home link and already on home page, scroll to top
    if (href === "/" && location.pathname === "/") {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    
    // If it's the contact link
    if (href === "/#contact") {
      e.preventDefault();
      if (location.pathname === "/") {
        // Already on home page, just scroll
        const element = document.querySelector("#contact");
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      } else {
        // Navigate to home with hash
        navigate("/#contact");
      }
    }
  };

  const treatmentLinks = [
    { name: t('nav.treatments.dentalImplants'), href: "/treatments/dental-implants" },
    { name: t('nav.treatments.allOn4'), href: "/treatments/all-on-4" },
    { name: t('nav.treatments.allOnX'), href: "/treatments/all-on-x" },
    { name: t('nav.treatments.fullMouth'), href: "/treatments/full-mouth-restoration" },
    { name: t('nav.treatments.veneers'), href: "/treatments/veneers" },
    { name: t('nav.treatments.crowns'), href: "/treatments/crowns" },
    { name: t('nav.treatments.rootCanal'), href: "/treatments/root-canal" },
    { name: t('nav.treatments.smileDesign'), href: "/treatments/smile-design" },
    { name: t('nav.treatments.implantBridge'), href: "/implant-supported-bridge" },
    { name: t('nav.treatments.teethWhitening'), href: "/treatments/teeth-whitening" },
  ];

  const navLinks = [
    { name: t('nav.home'), href: "/" },
    { name: t('nav.ourClinic'), href: "/our-clinic" },
    { name: t('nav.ourTeam'), href: "/our-team" },
    { name: t('nav.smileGallery'), href: "/smile-gallery" },
    { name: t('nav.blog'), href: "/blog" },
    { name: t('nav.contact'), href: "/#contact" },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "bg-background/95 backdrop-blur-sm shadow-sm" : "bg-transparent"
      }`}
    >
      <div className="container mx-auto px-4 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center" aria-label="LS Dental Clinic - Go to homepage">
            <img src={logo} alt="LS Dental Clinic Logo" className="h-12 w-auto" width={120} height={48} />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.slice(0, 2).map((link) => (
              <Link
                key={link.name}
                to={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className="text-sm font-medium text-foreground hover:text-primary transition-colors"
              >
                {link.name}
              </Link>
            ))}
            
            {/* Dental Treatments Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger className="text-sm font-medium text-foreground hover:text-primary transition-all duration-200 flex items-center gap-1 outline-none group">
                {t('nav.dentalTreatments')}
                <ChevronDown className="h-4 w-4 transition-transform duration-200 group-data-[state=open]:rotate-180" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-background/95 backdrop-blur-md border-border shadow-xl z-[100] min-w-[240px] animate-in fade-in-0 zoom-in-95 slide-in-from-top-2 duration-200">
                {treatmentLinks.map((treatment, index) => (
                  <DropdownMenuItem 
                    key={treatment.name} 
                    asChild
                    className="animate-in fade-in-0 slide-in-from-top-1"
                    style={{ animationDelay: `${index * 30}ms` }}
                  >
                    <Link
                      to={treatment.href}
                      className="text-sm font-medium text-foreground hover:text-primary cursor-pointer w-full"
                    >
                      {treatment.name}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {navLinks.slice(2).map((link) => (
              <Link
                key={link.name}
                to={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className="text-sm font-medium text-foreground hover:text-primary transition-colors"
              >
                {link.name}
              </Link>
            ))}
            <LanguageSelector />
            <div className="hidden lg:flex items-center gap-2 ml-2">
              <a
                href="https://wa.me/50683541716"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="WhatsApp +506 8354-1716"
                title="WhatsApp +506 8354-1716"
              >
                <Button variant="outline" size="icon" className="h-10 w-10">
                  <MessageCircle className="w-5 h-5" />
                </Button>
              </a>
              <a
                href="tel:+18336484745"
                aria-label={`${t('nav.tollFree')} +1 833-648-4745`}
                title={`${t('nav.tollFree')} +1 833-648-4745`}
              >
                <Button variant="outline" size="icon" className="h-10 w-10">
                  <Phone className="w-5 h-5" />
                </Button>
              </a>
            </div>
            <Button size="lg" className="ml-2" onClick={(e) => handleNavClick(e, '/#contact')}>
              {t('nav.bookAppointment')}
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-background border-t">
          <div className="container mx-auto px-4 py-4 flex flex-col gap-4">
            {navLinks.slice(0, 2).map((link) => (
              <Link
                key={link.name}
                to={link.href}
                className="text-sm font-medium text-foreground hover:text-primary transition-colors py-2"
                onClick={(e) => handleNavClick(e, link.href)}
              >
                {link.name}
              </Link>
            ))}
            
            {/* Dental Treatments Mobile Submenu */}
            <div className="flex flex-col">
              <span className="text-sm font-medium text-foreground py-2">
                {t('nav.dentalTreatments')}
              </span>
              <div className="pl-4 flex flex-col gap-2">
                {treatmentLinks.map((treatment) => (
                  <Link
                    key={treatment.name}
                    to={treatment.href}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors py-1"
                    onClick={(e) => handleNavClick(e, treatment.href)}
                  >
                    {treatment.name}
                  </Link>
                ))}
              </div>
            </div>

            {navLinks.slice(2).map((link) => (
              <Link
                key={link.name}
                to={link.href}
                className="text-sm font-medium text-foreground hover:text-primary transition-colors py-2"
                onClick={(e) => handleNavClick(e, link.href)}
              >
                {link.name}
              </Link>
            ))}
            <div className="flex items-center justify-between py-2">
              <span className="text-sm font-medium text-foreground">Language / Idioma</span>
              <LanguageSelector />
            </div>
            <a href="https://wa.me/50683541716" target="_blank" rel="noopener noreferrer" className="w-full">
              <Button variant="outline" className="w-full gap-2">
                <MessageCircle className="w-4 h-4" />
                WhatsApp +506 8354-1716
              </Button>
            </a>
            <a href="tel:+18336484745" className="w-full">
              <Button variant="outline" className="w-full gap-2">
                <Phone className="w-4 h-4" />
                {t('nav.tollFree')} +1 833-648-4745
              </Button>
            </a>
            <Button className="w-full" onClick={(e) => handleNavClick(e, '/#contact')}>{t('nav.bookAppointment')}</Button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
